import { useDispatch, useSelector } from 'react-redux';

import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type Fleet } from '../domain/fleet';
import { type VehicleType } from '../domain/vehicle';

import { setLoader, toggleToast, unsetLoader } from '@/common/application/toastSlice';
import { RequestTypeEnum } from '@/common/domain/toast';
import useFleetApi from '@/fleet/data/fleetApi';
import { createEmptyPromise, type SelfResolvingPromise } from '@/group/data/groupStore';


export interface FleetStore {
    isLoading: boolean;
    contextFleetMap: {[key: string]: Fleet},
    contextVehicleTypesMap: {[key: string]: VehicleType},

}

const initialState: FleetStore = {
    contextFleetMap: {},
    contextVehicleTypesMap: {},
    isLoading: false,
};

const sideState = {
    keyLoader: {} as {[key: string]: (SelfResolvingPromise<unknown> | undefined)}
};

const fleetStore = createSlice({
    initialState,
    name: 'fleetStore',
    reducers: {
        clearStore() {
            return initialState;
        },
        deleteFleet(state, action: PayloadAction<number | string>) {
            if (state.contextFleetMap[String(action.payload)]) delete state.contextFleetMap[String(action.payload)];
        },
       
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        clearLoadingKey(state, action: PayloadAction<string>) {
            delete sideState.keyLoader[action.payload];
        },
        setLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload] = createEmptyPromise();
            return { ...state };
        },
        failLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload]?.reject(new Error('fail to load fleet'));
            delete sideState.keyLoader[action.payload];
        },
        upsertFleet(state, action: PayloadAction<Fleet>) {
            const prk = action.payload;
            if (prk.documentId) state.contextFleetMap[prk.documentId] = {...prk};
            if (prk.documentId && sideState.keyLoader[prk.documentId]) {
                sideState.keyLoader[prk.documentId]?.resolve({...prk});
                delete sideState.keyLoader[prk.documentId];
            }
        },
        upsertFleets(state, action: PayloadAction<Fleet[]>) {
            const newFleetsList:{[key: string]: Fleet} = {...state.contextFleetMap};
            for (const prk of action.payload) {
                if (prk.documentId) newFleetsList[prk.documentId] = { ...prk};
            }
            state.contextFleetMap = newFleetsList;
            if (sideState.keyLoader['allfleets']) {
                sideState.keyLoader['allfleets'].resolve(Object.values(newFleetsList));
                delete sideState.keyLoader['allfleets'];
            }
        },
        upsertVehicleTypes(state, action: PayloadAction<VehicleType[]>) {
            const newVehicleTypesList:{[key: string]: VehicleType} = {...state.contextVehicleTypesMap};
            for (const prk of action.payload) {
                if (prk.id) newVehicleTypesList[prk.id] = { ...prk};
            }
            state.contextVehicleTypesMap = newVehicleTypesList;
            if (sideState.keyLoader['allvehicletypes']) {
                sideState.keyLoader['allvehicletypes'].resolve(Object.values(newVehicleTypesList));
                delete sideState.keyLoader['allvehicletypes'];
            }
        }
    },
    selectors: {
        getFleets: createSelector([state => state.fleetStore?.contextFleetMap], (contextFleetMap): () => Fleet[] => {
            return () => contextFleetMap ? Object.values<Fleet>(contextFleetMap) : [];
        }),
        getFleetById: createSelector([state => state.fleetStore?.contextFleetMap], (contextFleetMap): (id: string | number) => Fleet | null => {
            return (id: string | number) => contextFleetMap ? contextFleetMap[String(id)] : null;
        }),
        getVehicleTypes: createSelector([state => state.fleetStore?.contextVehicleTypesMap], (contextVehicleTypesMap): () => VehicleType[] => {
            return () => contextVehicleTypesMap ? Object.values<VehicleType>(contextVehicleTypesMap) : [];
        }),
    }
});


export const { upsertFleets, failLoadingKey, upsertVehicleTypes, setLoadingKey, upsertFleet, deleteFleet } = fleetStore.actions;
export default fleetStore.reducer;


export function useFleetStore() {
    
    const { apiGetFleet, apiGetFleets, apiGetVehicleTypes } = useFleetApi();
    
    
    const dispatch = useDispatch();
    const { getFleetById, getFleets: getFleetsSelector, getVehicleTypes: getVehicleTypesSelector } = fleetStore.getSelectors();

    const findPrkById = useSelector(getFleetById);
    const findPrks = useSelector(getFleetsSelector);
    const findVehicleTypes = useSelector(getVehicleTypesSelector);

    return {
        async deleteFleet(id: number | string) {
            return dispatch(deleteFleet(id));
        },
       
        async getFleets(force: boolean = false) {
            const foundflts = findPrks();
            if (foundflts.length > 0 && !force) {
                return foundflts;
            } else if (!force && typeof sideState.keyLoader['allfleets'] !== 'undefined') {
                return (await sideState.keyLoader['allfleets']) as Fleet[];
            } else {
                try {
                    dispatch(setLoadingKey('allfleets'));
                    dispatch(setLoader());
                    const fltsResp = await apiGetFleets();
                    if (fltsResp.status.code < 400 && Array.isArray(fltsResp.data)) {
                        dispatch(upsertFleets(fltsResp?.data));
                        
                    } else {
                        dispatch(failLoadingKey('allfleets'));
                    }
                    dispatch(unsetLoader());
                    return fltsResp?.data;
                } catch (error: any) {
                    dispatch(unsetLoader());
                    dispatch(failLoadingKey('allfleets'));
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getFleets}));
                    }
                }
                
            }
        },

        async getFleetById(documentId: string, force: boolean = false) {
            
            const loadFltFromApi = async () => {
                try {
                    dispatch(setLoadingKey(documentId));
                    dispatch(setLoader());
                    const fleetResp = await apiGetFleet(documentId);
    
                    if (fleetResp.status.code < 400 && fleetResp.data.id) {
                        dispatch(upsertFleet(fleetResp?.data));
                        dispatch(unsetLoader());
                        return fleetResp.data;
                    }
                } catch (error: any) {
                    dispatch(unsetLoader());
                    dispatch(failLoadingKey(documentId));
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getFleet}));
                    }
                }
            };
            if (force) {
                return await loadFltFromApi();
            } else {
                const prkg = findPrkById(documentId);
                if (prkg?.documentId === documentId) return prkg;
                if (typeof sideState.keyLoader[documentId] !== 'undefined') return await sideState.keyLoader[documentId] as Fleet;

                return await loadFltFromApi();
            }
            
        },

        async getVehicleTypes(force: boolean = false) {
            const foundVTs = findVehicleTypes();
            if (foundVTs.length > 0 && !force) {
                return foundVTs;
            } else if (!force && typeof sideState.keyLoader['allvehicletypes'] !== 'undefined') {
                return await sideState.keyLoader['allvehicletypes'] as VehicleType[];
            } else {
                try {
                    dispatch(setLoadingKey('allvehicletypes'));
                    dispatch(setLoader());
                    const fltsResp = await apiGetVehicleTypes();
                    if (fltsResp.status.code < 400 && Array.isArray(fltsResp.data)) {
                        dispatch(upsertVehicleTypes(fltsResp?.data));
                    } else {
                        dispatch(failLoadingKey('allvehicletypes'));
                    }
                    dispatch(unsetLoader());
                    return fltsResp?.data;
                } catch (error: any) {
                    dispatch(unsetLoader());
                    dispatch(failLoadingKey('allvehicletypes'));
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getFleets}));
                    }
                }
                
            }
        }

    };
}